import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const READINESS_CLS = {
  CAPITAL_REVIEW_READY: "text-emerald-200 bg-emerald-500/15 border-emerald-400/40",
  MATERIAL_GAPS_REMAIN: "text-amber-200 bg-amber-500/15 border-amber-400/40",
  RESEARCH_IN_PROGRESS: "text-sky-200 bg-sky-500/12 border-sky-400/30",
  EARLY_RESEARCH: "text-zinc-300 bg-zinc-500/10 border-zinc-500/30",
  CAPITAL_REVIEW_BLOCKED: "text-rose-200 bg-rose-500/15 border-rose-400/45",
};
const GAP_CLS = { BLOCKER: "text-rose-300", IMPORTANT: "text-amber-300", USEFUL: "text-sky-300", INFORMATIONAL: "text-zinc-400" };
const DECISION_PLAIN = {
  CONTINUE_RESEARCH: "Continue research", READY_FOR_CONTROLLED_TEST_CONSIDERATION: "Ready for controlled test consideration",
  PUT_ON_WATCH: "Put on watch", REJECT_FOR_NOW: "Reject for now", BLOCKED: "Blocked",
};

export default function CapitalReview() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const productId = params.get("product") || "cp:portable_blender";
  const [price, setPrice] = useState("49");
  const [maxCap, setMaxCap] = useState("2000");
  const [include, setInclude] = useState([]);
  const [d, setD] = useState(null);
  const [reasoning, setReasoning] = useState("");
  const [changeMind, setChangeMind] = useState("");
  const [history, setHistory] = useState([]);

  const load = () => {
    const p = { product_id: productId };
    if (price) p.selling_price = parseFloat(price);
    if (maxCap) p.max_test_capital = parseFloat(maxCap);
    if (include.length) p.include_compliance = include.join(",");
    api.get("/capital-review/product", { params: p }).then((r) => setD(r.data)).catch(() => setD(null));
    api.get("/capital-review/history", { params: { product_id: productId } }).then((r) => setHistory(r.data.history || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId, include]);

  const toggle = (cat) => setInclude((p) => p.includes(cat) ? p.filter((x) => x !== cat) : [...p, cat]);
  const decide = async (decision) => {
    await api.post("/capital-review/decision", { product_id: productId, decision, selling_price: parseFloat(price) || null,
      reasoning: reasoning.trim() || null, what_would_change_my_mind: changeMind.trim() || null });
    toast.success(`Decision recorded: ${DECISION_PLAIN[decision]}`); setReasoning(""); load();
  };

  if (!d) return <div className="p-8 text-[#71717A] font-mono text-sm" data-testid="capital-loading">Loading capital review…</div>;
  const Money = ({ v }) => <span>{v == null ? "—" : `$${v}`}</span>;
  const rd = d.readiness;

  return (
    <div className="p-8 max-w-[1100px]" data-testid="capital-review-page">
      <button onClick={() => nav(`/graph`)} className="text-[11px] font-mono text-[#71717A] hover:text-[#A1A1AA] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Product Evidence</button>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-head text-3xl font-bold capitalize">{d.product} — Capital Review</h1>
          <div className="text-[11px] font-mono text-[#52525B] mt-1">Are we ready to make a money decision? — {d.note}</div>
        </div>
        <button onClick={() => nav(`/opportunity-brief?product=${productId}`)} data-testid="create-opportunity-brief-btn"
          className="text-[11px] font-mono text-sky-200 border border-sky-400/40 bg-sky-500/10 px-3 py-1.5 hover:bg-sky-500/20 whitespace-nowrap">
          Create Opportunity Brief
        </button>
      </div>

      {/* readiness */}
      <div className={`border p-4 mt-4 ${READINESS_CLS[rd.state]}`} data-testid="capital-readiness">
        <div className="text-lg font-head font-semibold">{rd.state_plain}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-xs">
          <div><div className="text-[10px] uppercase tracking-widest opacity-70 font-mono mb-1">Completed</div>
            {rd.completed.map((c, i) => <div key={i}>✓ {c}</div>)}</div>
          <div><div className="text-[10px] uppercase tracking-widest opacity-70 font-mono mb-1">Needs work</div>
            {rd.needs_work.length === 0 ? <div>— none —</div> : rd.needs_work.map((c, i) => <div key={i} className={GAP_CLS[c.gap_class] || ""}>! {c.area}</div>)}</div>
        </div>
        <div className="text-[10px] font-mono opacity-70 mt-2">{rd.note}</div>
      </div>

      {/* capital blockers */}
      {d.risk_summary.capital_blockers.length > 0 && (
        <div className="border border-rose-400/45 bg-rose-500/[0.06] p-3 mt-3" data-testid="capital-blockers">
          <div className="text-sm text-rose-200 font-semibold">Money decision blocked</div>
          {d.risk_summary.capital_blockers.map((b, i) => <div key={i} className="text-xs text-rose-200 mt-1">{b} — resolve before committing inventory capital.</div>)}
        </div>
      )}

      {/* inputs */}
      <div className="flex flex-wrap items-end gap-3 mt-4">
        <label className="text-xs text-[#A1A1AA]">Selling price scenario
          <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} onBlur={load} data-testid="capital-price"
            className="block mt-1 bg-[#18181B] border border-[#3f3f46] px-2 py-1 text-xs w-24 font-mono" /></label>
        <label className="text-xs text-[#A1A1AA]">Max I'm willing to risk
          <input type="number" value={maxCap} onChange={(e) => setMaxCap(e.target.value)} onBlur={load} data-testid="capital-maxcap"
            className="block mt-1 bg-[#18181B] border border-[#3f3f46] px-2 py-1 text-xs w-28 font-mono" /></label>
      </div>

      {/* one-page grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <Panel title="Why we're interested" testid="capital-why">
          {d.why_interested.map((w, i) => <div key={i} className="text-xs text-[#A1A1AA]">• {w}</div>)}
        </Panel>
        <Panel title="What customers tell us" testid="capital-customers">
          <div className="text-[11px] text-[#71717A]">Problems:</div>
          {d.customer_opportunity.problems.map((p, i) => <div key={i} className="text-xs text-[#A1A1AA] capitalize">{p.label} — {p.unique_complaints} complaints</div>)}
          <div className="text-[11px] text-[#71717A] mt-1">Customers value: <span className="text-emerald-300 capitalize">{d.customer_opportunity.love.join(", ") || "—"}</span></div>
        </Panel>
        <Panel title="Version we're considering" testid="capital-version">
          {d.proposed_version ? <div className="text-xs text-[#A1A1AA]">{d.proposed_version.name} <span className="text-[#71717A]">({d.proposed_version.improvements.join(", ") || "no changes"})</span></div> : <div className="text-xs text-[#52525B]">No concept version created yet.</div>}
        </Panel>
        <Panel title="Can we source it?" testid="capital-source">
          <div className="text-xs text-[#A1A1AA]">{d.sourceability.suppliers_observed} suppliers observed · confidence {d.sourceability.confidence.toLowerCase()}</div>
          <div className="text-[10px] text-[#71717A] mt-0.5">{d.sourceability.reliability}</div>
        </Panel>
        <Panel title="Current economics" testid="capital-economics">
          <div className="text-xs text-[#A1A1AA]">Sell <Money v={d.economics.selling_price} /> · landed <Money v={d.economics.modeled_landed_cost} /></div>
          <div className="text-sm text-emerald-300 mt-0.5">Money before ads <Money v={d.economics.money_before_ads} /></div>
          <div className="text-[11px] text-sky-300">Most you can pay per sale on ads <Money v={d.economics.breakeven_ad_ceiling} /></div>
        </Panel>
        <Panel title="Cash we'd risk" testid="capital-cash">
          {d.cash_commitment.paths.slice(0, 4).map((p, i) => (
            <div key={i} className="text-xs text-[#A1A1AA] flex items-center gap-2">
              <span>{p.quantity} units · <Money v={p.known_inventory_cash} /></span>
              <span className={`text-[9px] font-mono px-1 ${p.capital_flag === "ABOVE_LIMIT" ? "text-rose-300" : p.capital_flag === "WITHIN_LIMIT" ? "text-emerald-300" : "text-zinc-500"}`}>
                {p.capital_flag === "ABOVE_LIMIT" ? "over budget" : p.capital_flag === "WITHIN_LIMIT" ? "within budget" : "costs incomplete"}</span>
            </div>
          ))}
          <div className="text-[10px] text-[#71717A] mt-0.5">{d.cash_commitment.note}</div>
        </Panel>
      </div>

      {/* compliance cost toggle */}
      <Panel title="Include compliance/testing costs in the scenario?" testid="capital-compliance" className="mt-3">
        <div className="flex flex-wrap gap-2">
          {d.compliance_scenario.applied_costs.length === 0 && (d.risk_summary.important || []).length === 0 && <span className="text-xs text-[#52525B]">No mitigation costs identified.</span>}
          {(d.compliance_scenario.applied_costs || []).map((m, i) => <span key={i} className="text-[10px] text-emerald-300 font-mono">✓ {m.label} (${m.amount})</span>)}
        </div>
        {d.risk_summary.important.map((r) => (
          <button key={r.category} onClick={() => toggle(r.category)} data-testid={`capital-include-${r.category}`}
            className={`text-[10px] font-mono px-2 py-1 border mr-2 mt-2 ${include.includes(r.category) ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-[#3f3f46] text-[#71717A]"}`}>
            {include.includes(r.category) ? "Included" : "Include"}: {r.category.replace(/_/g, " ").toLowerCase()}
          </button>
        ))}
        <div className="text-xs text-[#A1A1AA] mt-2">
          Base money before ads <Money v={d.compliance_scenario.base_money_before_ads} /> · with selected costs <span className="text-amber-300"><Money v={d.compliance_scenario.scenario_money_before_ads} /></span>
          {d.compliance_scenario.added_fixed_cost ? ` · +$${d.compliance_scenario.added_fixed_cost} one-time` : ""}
        </div>
        <div className="text-[10px] text-[#52525B] font-mono mt-1">{d.compliance_scenario.note}</div>
      </Panel>

      {/* risks + gaps + unknowns */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        <Panel title="What could stop us?" testid="capital-risk">
          <div className={`text-xs ${d.risk_summary.blocking ? "text-rose-300" : "text-[#A1A1AA]"}`}>{d.risk_summary.headline}</div>
          {d.risk_summary.important.map((r, i) => <div key={i} className="text-[11px] text-amber-300">• {r.title}</div>)}
        </Panel>
        <Panel title="Important gaps" testid="capital-gaps">
          {d.gaps.gaps.length === 0 ? <div className="text-xs text-[#52525B]">None</div> : d.gaps.gaps.map((g, i) => (
            <div key={i} className={`text-[11px] ${GAP_CLS[g.class]}`}>[{g.class.toLowerCase()}] {g.gap}</div>
          ))}
        </Panel>
        <Panel title="Biggest unknowns" testid="capital-unknowns">
          {d.biggest_unknowns.map((u, i) => <div key={i} className="text-[11px] text-[#A1A1AA]">{i + 1}. {u}</div>)}
        </Panel>
      </div>

      {/* pricing + evidence quality + freshness + negatives */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <Panel title="Pricing confidence" testid="capital-pricing">
          <div className="text-xs text-[#A1A1AA]">Proposed <Money v={d.pricing_evidence.proposed_price} /> · observed range {d.pricing_evidence.observed_competitor_range ? `$${d.pricing_evidence.observed_competitor_range[0]}–$${d.pricing_evidence.observed_competitor_range[1]}` : "—"}</div>
          {d.pricing_evidence.premium_is_assumption && <div className="text-[11px] text-amber-300 mt-1">{d.pricing_evidence.premium_is_assumption}</div>}
        </Panel>
        <Panel title="Evidence strength & freshness" testid="capital-evidence">
          <div className="text-xs text-[#A1A1AA]">{d.evidence_quality.plain} <span className="text-[#71717A]">— {d.evidence_quality.why}</span></div>
          {d.freshness.rows.filter((r) => r.refresh_hint).map((r, i) => <div key={i} className="text-[11px] text-amber-300 mt-1">{r.refresh_hint}</div>)}
        </Panel>
      </div>
      {d.negative_evidence.length > 0 && (
        <Panel title="Negative / cautionary evidence (kept visible)" testid="capital-negatives" className="mt-3">
          {d.negative_evidence.map((n, i) => <div key={i} className="text-[11px] text-rose-300/90">• {n}</div>)}
        </Panel>
      )}

      {/* test hypotheses */}
      <Panel title="If we test, what must it prove?" testid="capital-hypotheses" className="mt-3">
        {d.test_hypotheses.map((h, i) => <div key={i} className="text-[11px] text-[#A1A1AA]">• {h.hypothesis} <span className="text-[#52525B] font-mono">({h.type.toLowerCase()})</span></div>)}
        <div className="text-[10px] text-[#52525B] font-mono mt-1">These are hypotheses to test — not predictions.</div>
      </Panel>

      {/* decision */}
      <Panel title="My decision" testid="capital-decision" className="mt-3">
        <textarea value={reasoning} onChange={(e) => setReasoning(e.target.value)} placeholder="Reasoning (why continue / reject / test / watch)…"
          data-testid="capital-reasoning" className="w-full bg-[#18181B] border border-[#3f3f46] px-2 py-1 text-xs font-mono mb-2" rows={2} />
        <input value={changeMind} onChange={(e) => setChangeMind(e.target.value)} placeholder="What evidence would change my decision? (e.g. real CAC > $25)"
          data-testid="capital-changemind" className="w-full bg-[#18181B] border border-[#3f3f46] px-2 py-1 text-xs font-mono mb-2" />
        <div className="flex flex-wrap gap-2">
          {d.decision_options.map((opt) => (
            <button key={opt} onClick={() => decide(opt)} data-testid={`capital-decide-${opt}`}
              className="text-[11px] font-mono px-3 py-1.5 border border-[#3f3f46] text-[#A1A1AA] hover:bg-[#18181B]">
              {DECISION_PLAIN[opt]}
            </button>
          ))}
        </div>
        <div className="text-[10px] text-[#52525B] font-mono mt-2">The decision is yours — Breakout Radar never auto-decides. Each decision is snapshotted (append-only).</div>
      </Panel>

      {/* decision history */}
      {history.length > 0 && (
        <Panel title="Decision history" testid="capital-history" className="mt-3">
          {history.map((h, i) => <div key={i} className="text-[11px] text-[#A1A1AA]"><span className="text-[#52525B] font-mono">{(h.at || "").slice(0, 10)}</span> {DECISION_PLAIN[h.decision] || h.decision} — {h.reasoning || "—"} <span className="text-[#52525B]">(readiness: {h.evidence_reference?.readiness_state})</span></div>)}
        </Panel>
      )}
    </div>
  );
}

function Panel({ title, children, testid, className = "" }) {
  return (
    <div className={`border border-[#27272A] bg-[#101013] p-3 ${className}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-1.5">{title}</div>
      {children}
    </div>
  );
}
